/**
 * useFocusField — scroll a `data-field` input into view and focus it.
 *
 * Used by AdminView.jsx to support deep-linking from an IntegrationConfigRequired
 * 412 response: the URL carries `?focus=<field_name>`, and the admin tab's
 * render targets tag each input with `data-field="<field_name>"`. This hook
 * locates the matching element and scrolls + focuses it after render.
 *
 * Works inside both the integration Modal (apikeys cards) and inline forms
 * (rankings / dataplex / mcp tabs) because it uses document-level
 * querySelector — no ref-threading required.
 */
import { useEffect } from "react";

export function useFocusField(focusedField, deps = []) {
  useEffect(() => {
    if (!focusedField) return;

    // Poll briefly — the target element may mount after the query-param is
    // read (e.g. integration modal opens one tick after tab switch). Retry
    // up to ~500ms so the focus lands even during slightly-delayed renders.
    let attempt = 0;
    const tryFocus = () => {
      const el = document.querySelector(`[data-field="${focusedField}"]`);
      if (el) {
        try {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.focus({ preventScroll: true });
        } catch {
          // Non-focusable element (e.g. checkbox label) — scroll is still useful.
        }
        return true;
      }
      return false;
    };

    if (tryFocus()) return;
    const interval = setInterval(() => {
      attempt += 1;
      if (tryFocus() || attempt >= 10) clearInterval(interval);
    }, 50);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedField, ...deps]);
}
